/**
 * Visual bridge client.
 *
 * The *transport* half of the kiosk-visual feature. Owned by the SDK because it
 * is tied to the session lifecycle and to the correlation id (`cid`) that must
 * match between the injected session instructions and the WebSocket
 * registration — keeping both here makes that pairing impossible to get wrong.
 *
 * This module is deliberately content-agnostic: it receives `show_content`
 * events and forwards them to host-registered callbacks as
 * `{ kind, key, params }`. Resolving a `key` to actual media (e.g. from a CMS
 * like Strapi) and rendering it is the host application's responsibility.
 *
 * See ../../kiosk-visual-bridge/CONTRACT.md for the wire contract.
 */
import { getLogger } from './logger';
import type { VisualCatalogEntry, VisualContentCallback, VisualContentEvent } from './types';

const logger = getLogger('VisualBridge');

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY_MS = 1000;

/** Host subscribers. Persist across sessions — only `_resetState` clears them. */
const visualCallbacks: VisualContentCallback[] = [];

let socket: WebSocket | null = null;
let shouldReconnect = false;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let activeUrl: string | null = null;
let activeCid: string | null = null;

/**
 * Generate a session correlation id: `cid_` + 32 lowercase hex chars.
 * Matches the bridge's `^cid_[a-f0-9]{32}$` contract.
 */
export function generateCid(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return `cid_${hex}`;
}

/**
 * Build the per-session instructions that tell the agent to route visuals to
 * this screen. Injected via start-session `sessionOverrides` (append mode).
 *
 * When a host-provided `catalog` is passed, its exact keys/kinds are listed so
 * the LLM references real content; otherwise generic guidance is used.
 */
export function buildVisualInstructions(cid: string, catalog?: VisualCatalogEntry[]): string {
  const base =
    `You can show visuals on the citizen's screen. This session's screen channel is ` +
    `cid="${cid}". When you show a visual, call the show_content tool with cid="${cid}" ` +
    `and the exact key of the item. Never say the cid out loud.`;

  if (!catalog || catalog.length === 0) {
    return `${base} Use a key that identifies the content you are describing.`;
  }

  const lines = catalog.map((entry) => {
    const kind = entry.kind ? ` (${entry.kind})` : '';
    const description = entry.description ? `: ${entry.description}` : '';
    return `- key="${entry.key}"${kind}${description}`;
  });

  return (
    `${base} Only use keys from this list; if none fits, do not call the tool:\n` + lines.join('\n')
  );
}

/** Register a host callback invoked for each visual event. */
export function onVisualContent(callback: VisualContentCallback): void {
  visualCallbacks.push(callback);
}

function emit(event: VisualContentEvent): void {
  for (const cb of visualCallbacks) {
    try {
      cb(event);
    } catch (error) {
      logger.error('onVisualContent callback threw', {
        error: error instanceof Error ? error.message : error,
      });
    }
  }
}

/**
 * Open the visual bridge WebSocket for a session and begin emitting events.
 * Non-blocking and best-effort: failures are logged and retried, never thrown,
 * so a bridge problem can never break the audio session.
 */
export function connectVisualBridge(bridgeUrl: string, cid: string): void {
  activeUrl = bridgeUrl;
  activeCid = cid;
  shouldReconnect = true;
  reconnectAttempts = 0;
  openSocket();
}

function openSocket(): void {
  if (!activeUrl || !activeCid) return;
  const sep = activeUrl.includes('?') ? '&' : '?';
  const url = `${activeUrl}${sep}cid=${encodeURIComponent(activeCid)}`;
  logger.info('Connecting to visual bridge');

  try {
    socket = new WebSocket(url);
  } catch (error) {
    logger.error('Failed to open visual bridge socket', {
      error: error instanceof Error ? error.message : error,
    });
    scheduleReconnect();
    return;
  }

  socket.onopen = () => {
    reconnectAttempts = 0;
    logger.info('Visual bridge connected');
  };

  socket.onmessage = (ev: MessageEvent) => {
    const raw = typeof ev.data === 'string' ? ev.data : '';
    let msg: Record<string, unknown>;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      msg = parsed as Record<string, unknown>;
    } catch {
      logger.warn('Ignoring non-JSON visual bridge message');
      return;
    }

    const type = msg.type;
    if (type === 'show_content') {
      const key = typeof msg.key === 'string' ? msg.key : '';
      if (!key) {
        logger.warn('show_content event missing key; ignoring');
        return;
      }
      const event: VisualContentEvent = {
        kind: typeof msg.kind === 'string' ? msg.kind : 'image',
        key,
        params:
          msg.params && typeof msg.params === 'object'
            ? (msg.params as Record<string, unknown>)
            : {},
      };
      logger.info('Visual content event received', { kind: event.kind, key: event.key });
      emit(event);
    } else if (type === 'registered') {
      // cid logged at debug to aid local testing (e.g. targeting POST /dev/push).
      logger.debug('Visual bridge registered', { cid: activeCid });
    } else if (type === 'ping') {
      // keepalive; no action needed
    } else {
      logger.debug('Unknown visual bridge message type', { type });
    }
  };

  socket.onclose = () => {
    logger.info('Visual bridge socket closed', { willReconnect: shouldReconnect });
    socket = null;
    scheduleReconnect();
  };

  socket.onerror = () => {
    // onclose fires next and handles reconnect; just note it here.
    logger.warn('Visual bridge socket error');
  };
}

function scheduleReconnect(): void {
  if (!shouldReconnect) return;
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    logger.warn('Visual bridge giving up after max reconnect attempts', {
      attempts: reconnectAttempts,
    });
    return;
  }
  reconnectAttempts += 1;
  const delay = BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts - 1);
  logger.info('Scheduling visual bridge reconnect', { attempt: reconnectAttempts, delay });
  reconnectTimer = setTimeout(openSocket, delay);
}

/** Close the socket and stop reconnecting. Called on session cleanup. */
export function disconnectVisualBridge(): void {
  shouldReconnect = false;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    try {
      socket.close();
    } catch {
      // ignore
    }
    socket = null;
  }
  activeUrl = null;
  activeCid = null;
  logger.debug('Visual bridge disconnected');
}

/** Reset all module state, including host callbacks. For tests only. */
export function _resetState(): void {
  disconnectVisualBridge();
  visualCallbacks.length = 0;
  reconnectAttempts = 0;
}
